// Libraries
import { Drawer } from "@lockerpm/design";
import { useEffect, useState, useMemo } from "react";

// Resources
import { ReactComponent as CloseLine } from "#src/assets/images/icons/close-line.svg";

// General
import type { IDrawerAdapterProps } from "#src/@types/common";

// Components
import { IconButton } from "#src/common/system/Button";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import LoadingState from "#src/common/system/LoadingState";

import { DomainDrawerTitle, DomainDrawerContent, DomainDrawerFooter } from "./VerifyDomain";
import { IpAddressDrawerTitle, IpAddressDrawerContent, IpAddressDrawerFooter } from "./VerifyIpAdress";

// API-related
import assetsServices, {
  type IDomainOwnershipItem,
} from "#src/services/assets";

interface IVerifyOwnershipDrawerProps extends IDrawerAdapterProps {
  assetName: string | null;
  assetId: number | null;
  assetType?: string;
}

const VerifyOwnershipDrawer = ({
  open,
  onClose,
  workspaceId,
  assetName,
  assetId,
  onRefresh,
}: IVerifyOwnershipDrawerProps) => {
  
  const [assetOwnership, setAssetOwnership] = useState<IDomainOwnershipItem | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const isIpAddress = useMemo(() => {
    return assetOwnership?.name === 'html_file'
  }, [assetOwnership])

  useEffect(() => {
    const getAssetOwnerships = () => {
      setLoading(true);
      if (assetId)
        assetsServices
          .list_domain_ownerships(assetId, workspaceId)
          .then((response) => {
            // currently there's only 1 way to verify ownership, so we only have 1 item
            setAssetOwnership(response[0]);
          })
          .catch(apiErrorHandler)
          .finally(() => {
            setLoading(false);
          }
      )
      setLoading(false);
    };
    getAssetOwnerships();
  }, [assetId, workspaceId]);

  const onCloseDrawerAndCleanup = () => {
    setAssetOwnership(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onCloseDrawerAndCleanup}
      closable={false}
      title={!loading && !!assetOwnership && <>
        {
          isIpAddress ? <IpAddressDrawerTitle /> : <DomainDrawerTitle />
        }
      </>}
      extra={
        <IconButton
          variant="label-grey"
          size={9}
          onClick={onCloseDrawerAndCleanup}
        >
          <CloseLine />
        </IconButton>
      }
      footer={ !loading && !!assetOwnership && <>
        {
          isIpAddress ? <IpAddressDrawerFooter
            workspaceId={workspaceId}
            assetId={assetId}
            onClose={onCloseDrawerAndCleanup}
            onRefresh={onRefresh}
          /> : <DomainDrawerFooter
            workspaceId={workspaceId}
            assetId={assetId}
            onClose={onCloseDrawerAndCleanup}
            onRefresh={onRefresh}
          />
        }
      </>}
    >
      {
        (loading || !assetOwnership) ? <LoadingState /> : <>
          {
            isIpAddress ? <IpAddressDrawerContent
              assetName={assetName}
              assetOwnership={assetOwnership}
            /> : <DomainDrawerContent
              assetName={assetName}
              assetOwnership={assetOwnership}
            />
          }
        </>
      }
    </Drawer>
  );
};

export default VerifyOwnershipDrawer;
